import { X } from "lucide-react";
import { TONE_SOFT } from "shared/tones";

/** Reduce one typed/pasted token to a bare lowercase domain — no scheme, `www.`, path or port.
 * Best-effort only: the server is the real validator and rejects anything unusable. */
export function cleanDomain(raw: string): string {
  let text = raw.trim();
  if (!text) return "";
  try {
    text = new URL(text.includes("//") ? text : `http://${text}`).hostname;
  } catch {
    // leave as typed; the server will reject it with a clear message
  }
  return text.toLowerCase().replace(/^(www\.)+/, "");
}

/** `value` plus every domain found in `text` (split on spaces, commas, semicolons, newlines), cleaned and deduped. */
export function withDomains(value: string[], text: string): string[] {
  const next = [...value];
  for (const token of text.split(/[\s,;]+/)) {
    const cleaned = cleanDomain(token);
    if (cleaned && !next.includes(cleaned)) next.push(cleaned);
  }
  return next;
}

interface Props {
  value: string[];
  onChange: (domains: string[]) => void;
  /** What's typed but not yet turned into a chip. Controlled by the parent so a Save
   * can include it (see withDomains) instead of losing it. */
  draft: string;
  onDraftChange: (draft: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

/** Chip list of domains: type or paste (several at once is fine), press Enter / comma (or leave the box) to add, click × to remove. */
export default function DomainChipsInput({ value, onChange, draft, onDraftChange, disabled, placeholder }: Props) {
  function commit() {
    if (!draft.trim()) return;
    onChange(withDomains(value, draft));
    onDraftChange("");
  }

  return (
    <div className="w-full bg-surface border border-border rounded-lg px-2 py-1.5 flex flex-wrap items-center gap-1.5 focus-within:border-primary focus-within:ring-1 focus-within:ring-ring transition-colors">
      {value.map((d) => (
        <span key={d} className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs ${TONE_SOFT.brand}`}>
          {d}
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(value.filter((x) => x !== d))}
            className="opacity-70 hover:opacity-100 hover:text-destructive disabled:opacity-40"
            aria-label={`Remove ${d}`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        disabled={disabled}
        onChange={(e) => onDraftChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit();
          } else if (e.key === "Backspace" && !draft && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={commit}
        placeholder={value.length === 0 ? placeholder ?? "acme.com" : ""}
        className="flex-1 min-w-[120px] bg-transparent text-sm text-foreground placeholder-muted-foreground px-1 py-1 focus:outline-none"
      />
    </div>
  );
}
